import { IIntegrationQueueEntry } from "../../../@types/admin/AdminIntegrationQueue";
import { useIntegrationMessagePayload } from "../../../hooks/queries/admin/integrationQueue";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  ModalContainer,
  ModalHeader,
  ModalJsonContainer,
  ModalLabel,
  ModalSubtitle,
  ModalTitle,
  ModalValue,
  ReadOnlyValue,
} from "../shared";

interface IIntegrationMessagePayloadModalProps {
  entry: IIntegrationQueueEntry;
}

/**
 * The raw payload of one inbound integration message (EN-5174).
 *
 * Retool's equivalent query was broken and always returned nothing: it
 * filtered on the process name AND the row id AND `Status = 'Valid'`, then
 * took the newest — so for the Invalid row you actually wanted to inspect,
 * the one filter that mattered excluded it. Here the id alone selects the
 * row, which is what the operator asked for by clicking it.
 *
 * The payload is HL7 pipe-delimited text, not JSON, so it is shown verbatim
 * in a monospace block. `ModalJsonContainer` is reused for the styling only.
 */
export function IntegrationMessagePayloadModal({
  entry,
}: IIntegrationMessagePayloadModalProps) {
  const { messagePayload, isLoading, isError } = useIntegrationMessagePayload(
    entry.id
  );

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Integration message</ModalTitle>
        <ModalSubtitle>
          {entry.processName ?? "Unknown process"} ·{" "}
          {getLongFormattedDate(entry.created)}
        </ModalSubtitle>
      </ModalHeader>

      <div>
        <ModalLabel>Message id</ModalLabel>
        <ModalValue>{entry.id}</ModalValue>
      </div>

      {entry.errorDescription ? (
        <div>
          <ModalLabel>Parse error</ModalLabel>
          <ModalValue>{entry.errorDescription}</ModalValue>
        </div>
      ) : null}

      {entry.queueErrorDescription ? (
        <div>
          <ModalLabel>Delivery error</ModalLabel>
          <ModalValue>{entry.queueErrorDescription}</ModalValue>
        </div>
      ) : null}

      <div>
        <ModalLabel>Payload</ModalLabel>
        {isLoading ? <ReadOnlyValue>Loading payload…</ReadOnlyValue> : null}
        {isError ? (
          <ErrorMessage>
            Couldn&apos;t load this message&apos;s payload.
          </ErrorMessage>
        ) : null}
        {!isLoading && !isError ? (
          <ModalJsonContainer>
            {messagePayload?.fileContent || "(empty payload)"}
          </ModalJsonContainer>
        ) : null}
      </div>
    </ModalContainer>
  );
}
